const FALLBACK_TIPS = [
  { id: "1", import: 1 },
  { id: "2", import: 2 },
  { id: "4", import: 4 },
  { id: "5", import: 5 },
  { id: "10", import: 10 },
  { id: "20", import: 20 },
];

export const CUSTOM_TIP_ID = "custom";
export const MIN_CUSTOM_TIP = 1;
export const MAX_CUSTOM_TIP = 999;

function toCoinId(value) {
  if (value == null) return null;
  if (typeof value === "object" && value._id != null) return String(value._id);
  return String(value);
}

function isLegacyImportesPropina(importes = []) {
  if (!Array.isArray(importes) || !importes.length) return false;
  return importes[0]?.import != null && importes[0]?.coin == null;
}

function isBusinessImportesPropinaObject(importesPropina) {
  return (
    importesPropina != null &&
    !Array.isArray(importesPropina) &&
    importesPropina.coin != null
  );
}

function findImportesForCoin(entries = [], coinId) {
  if (!coinId || !Array.isArray(entries) || !entries.length) return null;

  const normalizedCoinId = toCoinId(coinId);
  const entry = entries.find(
    (item) => item?.coin != null && toCoinId(item.coin) === normalizedCoinId,
  );

  if (entry && Array.isArray(entry.valores) && entry.valores.length === 6) {
    return entry.valores;
  }

  return null;
}

export function getBusinessCoinId(business, paisCoinId = null) {
  if (isBusinessImportesPropinaObject(business?.importesPropina)) {
    return toCoinId(business.importesPropina.coin);
  }

  if (Array.isArray(business?.importesPropina) && business.importesPropina[0]?.coin) {
    return toCoinId(business.importesPropina[0].coin);
  }

  if (paisCoinId) return toCoinId(paisCoinId);
  return null;
}

export function resolveTipsFromBusiness(business, coin) {
  if (
    isBusinessImportesPropinaObject(business?.importesPropina) &&
    Array.isArray(business.importesPropina.valores) &&
    business.importesPropina.valores.length === 6
  ) {
    return business.importesPropina.valores;
  }

  const businessValores = findImportesForCoin(
    business?.importesPropina,
    coin?._id,
  );
  if (businessValores) return businessValores;

  if (isLegacyImportesPropina(business?.importesPropina)) {
    return business.importesPropina;
  }

  if (Array.isArray(coin?.valores) && coin.valores.length) {
    return coin.valores;
  }

  return FALLBACK_TIPS;
}

export function isPropinaEditable(business) {
  return (
    isBusinessImportesPropinaObject(business?.importesPropina) &&
    business.importesPropina.propinaEditable === true
  );
}

/** Interpreta el importe custom (acepta "," y "." como decimal). */
export function toCustomTipNumber(value) {
  if (value === "" || value == null) return NaN;
  if (typeof value === "number") return value;
  const normalized = String(value).trim().replace(",", ".");
  if (normalized === "" || normalized === "." || normalized === ",") return NaN;
  return Number(normalized);
}

export function roundCustomTipAmount(value) {
  const num = toCustomTipNumber(value);
  if (!Number.isFinite(num)) return NaN;
  return Math.round(num * 100) / 100;
}

export function isValidCustomTipAmount(value) {
  const num = toCustomTipNumber(value);
  if (!Number.isFinite(num)) return false;
  const rounded = Math.round(num * 100) / 100;
  if (Math.abs(rounded - num) > 1e-8) return false;
  return num >= MIN_CUSTOM_TIP && num <= MAX_CUSTOM_TIP;
}

/** null | "min" | "max" | "invalid" — vacío no es error (muestra helper). */
export function getCustomTipAmountError(value) {
  if (value === "" || value == null) return null;
  const num = toCustomTipNumber(value);
  if (!Number.isFinite(num)) return "invalid";
  if (num < MIN_CUSTOM_TIP) return "min";
  if (num > MAX_CUSTOM_TIP) return "max";
  return null;
}

/** Formatea el número interpretado para el CTA ("2,50"). */
export function formatCustomTipAmount(value, locale = "es-ES") {
  const num = roundCustomTipAmount(value);
  if (!Number.isFinite(num)) return "";
  const hasDecimals = Math.round(num * 100) % 100 !== 0;
  return num.toLocaleString(locale, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Sanitiza input: dígitos + un separador (,|.), máx. 2 decimales.
 * Conserva el separador que escribió el usuario.
 */
export function parseCustomTipInput(raw) {
  if (raw === "" || raw == null) return "";
  const cleaned = String(raw).replace(/[^\d.,]/g, "");
  if (!cleaned) return "";

  let sep = null;
  let intPart = "";
  let decPart = "";

  for (const ch of cleaned) {
    if (ch === "," || ch === ".") {
      if (sep === null) sep = ch;
      continue;
    }
    if (sep === null) intPart += ch;
    else if (decPart.length < 2) decPart += ch;
  }

  if (intPart.length > 1) {
    intPart = intPart.replace(/^0+/, "") || "0";
  }

  const numeric = Number(
    `${intPart || "0"}${sep != null ? `.${decPart}` : ""}`,
  );

  if (Number.isFinite(numeric) && numeric > MAX_CUSTOM_TIP) {
    const intNum = parseInt(intPart || "0", 10);
    if (intNum >= MAX_CUSTOM_TIP) {
      return String(MAX_CUSTOM_TIP);
    }
  }

  let out = intPart;
  if (sep != null) {
    if (out === "") out = "0";
    out += sep + decPart;
  }
  return out;
}
